import { useCallback, useMemo } from 'react'
import { atom, useAtomValue, useSetAtom } from 'jotai'

import type { SkillCompareSide } from '../hooks/skill-compare-api'
import { useStableConversationKey } from './chats'

/** A live pair this session opened, and every id each side's chat has been known by: the submit's temp id, then the backend's. */
export interface LocalComparePair {
  pairId: string
  comparisonId: string
  skillSlug: string
  leftIsNew: boolean
  openedAt: number
  chats: Record<SkillCompareSide, Array<string>>
}

const localPairsAtom = atom<Record<string, LocalComparePair>>({})

/** Comparisons this session started; the chat view polls each until it resolves. */
export function useActiveSkillComparisonIds(): Array<string> {
  const pairs = useAtomValue(localPairsAtom)
  return useMemo(() => [...new Set(Object.values(pairs).map((pair) => pair.comparisonId))], [pairs])
}

/** A pair already open keeps its chats: both sides of a scheduled pair adopt it, one chat at a time. */
export function useOpenLocalPair(): (pair: Omit<LocalComparePair, 'chats' | 'openedAt'>) => void {
  const set = useSetAtom(localPairsAtom)
  return useCallback(
    (pair) =>
      set((prev) =>
        prev[pair.pairId]
          ? prev
          : { ...prev, [pair.pairId]: { ...pair, openedAt: Date.now(), chats: { old: [], new: [] } } },
      ),
    [set],
  )
}

/** Records an id a side's chat is known by, so the bar can show the side before the gateway confirms it. */
export function useBindLocalSide(): (pairId: string, side: SkillCompareSide, chatId: string) => void {
  const set = useSetAtom(localPairsAtom)
  return useCallback(
    (pairId, side, chatId) =>
      set((prev) => {
        const pair = prev[pairId]
        if (!pair || pair.chats[side].includes(chatId)) return prev
        return { ...prev, [pairId]: { ...pair, chats: { ...pair.chats, [side]: [...pair.chats[side], chatId] } } }
      }),
    [set],
  )
}

export function useDismissSkillComparison(): (comparisonId: string) => void {
  const set = useSetAtom(localPairsAtom)
  return useCallback(
    (comparisonId) =>
      set((prev) => Object.fromEntries(Object.entries(prev).filter(([, pair]) => pair.comparisonId !== comparisonId))),
    [set],
  )
}

/** The newest pair and side a chat runs, or undefined when it is not a compare side. Survives the chat's temp→real id swap. */
export function useLocalCompareSide(
  chatId: string | undefined,
): (LocalComparePair & { side: SkillCompareSide }) | undefined {
  const pairs = useAtomValue(localPairsAtom)
  const key = useStableConversationKey(chatId)
  return useMemo(() => {
    if (!chatId || !key) return undefined
    // Newest first: running the skill again in the same chat makes that chat a side of a second pair.
    for (const pair of Object.values(pairs).sort((a, b) => b.openedAt - a.openedAt)) {
      const side = (['old', 'new'] as const).find((s) => pair.chats[s].includes(key) || pair.chats[s].includes(chatId))
      if (side) return { ...pair, side }
    }
    return undefined
  }, [pairs, key, chatId])
}
